import React, { useMemo, useState, useEffect, useCallback } from "react";
import Table from "../../../core/common/dataTable/index";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS, BASE_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import { tokenService } from "../../../core/utils/tokenService";

type OfficeOrder = {
  id: string;
  orderNumber: string;
  cycleId: string;
  issueDate: string;
  employees: number;
  status: "Generated" | "Sent" | "Acknowledged";
};

/* ================= COMPONENTS ================= */
const DashboardTile = ({
  title,
  icon,
  count,
  link,
  colorClass = "text-primary",
}: {
  title: string;
  icon?: string;
  count?: string | number;
  link: string;
  colorClass?: string;
}) => {
  return (
    <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-6 col-sm-6 mb-3">
      <div
        className="card h-100 border-0 shadow-sm hover-card"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          cursor: "pointer",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
        onClick={() => { if (link !== "#") window.location.href = link; }}
      >
        <div className="card-body d-flex align-items-center p-3">
          <div
            className={`d-flex align-items-center justify-content-center me-3 rounded-circle`}
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
              border: "1px solid rgba(0,0,0,0.03)"
            }}
          >
            {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
          </div>
          <div>
            <div className="text-muted small mb-1" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>{title}</div>
            <div className={`h4 fw-bold mb-0 ${colorClass}`}>{count}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="mb-4">
      <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </h5>
      <div className="row">{children}</div>
    </div>
  );
};

const OfficeOrders: React.FC = () => {
  const [query, setQuery] = useState<string>("");
  const [cyclesList, setCyclesList] = useState<any[]>([
    { id: 1, name: " " }
  ]);
  const [selectedCycleId, setSelectedCycleId] = useState<number>(1);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [query, selectedCycleId]);
  const [generatingOrder, setGeneratingOrder] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalEmployees, setModalEmployees] = useState<any[]>([]);
  const [fetchingDetails, setFetchingDetails] = useState<boolean>(false);
  const [activeModalCycleId, setActiveModalCycleId] = useState<string>("");

  // Load initiated cycles list from API or fallback to localStorage
  useEffect(() => {
    const loadCycles = async () => {
      let loaded = false;
      try {
        const response = await Api.get(API_URLS.PROMOTION.CYCLES, true);
        console.log("CYCLES API Response (Office Orders):", response);
        if (response && response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const list = response.data.data.map((c: any) => ({
            id: c.id,
            name: c.cycleName || (c.gradeTo ? `${c.gradeFrom} to ${c.gradeTo} Promotion - ${c.quarter} ${c.financialYear}` : `${c.gradeFrom || ""} Promotion - ${c.quarter} ${c.financialYear}`)
          }));
          setCyclesList(list);
          loaded = true;

          if (list.length > 0) {
            setSelectedCycleId(list[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch cycles from API, falling back to local storage", error);
      }

      if (!loaded) {
        const stored = localStorage.getItem("promotion_cycles");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const hasCycle1 = parsed.some((c: any) => c.id === 1);
              const list = hasCycle1 ? parsed : [{ id: 1, name: " " }, ...parsed];
              setCyclesList(list);
              if (list.length > 0) {
                setSelectedCycleId(list[0].id);
              }
            }
          } catch (e) {
            console.error("Failed to parse promotion cycles from localStorage", e);
          }
        }
      }
    };
    loadCycles();
  }, []);

  const [orders, setOrders] = useState<OfficeOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(false);

  const fetchPromotedCounts = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const response = await Api.get(API_URLS.PROMOTION.PROMOTED_COUNTS, true);
      console.log("PROMOTED_COUNTS API Response:", response);
      let list: any[] = [];
      if (response && response.data && Array.isArray(response.data.data)) {
        list = response.data.data;
      }

      const updatedList: OfficeOrder[] = await Promise.all(
        list.map(async (c: any) => {
          let count = c.promotedCount || 0;
          try {
            const genRes = await Api.get(
              API_URLS.PROMOTION.GENERATE_OFFICE_ORDER,
              [{ cycleId: Number(c.cycleId) }],
              true
            );
            const genEmps = genRes?.data?.data?.promotedEmployees;
            if (Array.isArray(genEmps) && genEmps.length > 0) {
              count = genEmps.length;
            }
          } catch (e) {
          }

          const fy = c.financialYear || "2025-2026";
          const fyYear = fy.includes("-") ? fy.split("-")[0] : fy;

          const statusVal: "Generated" | "Sent" | "Acknowledged" = (() => {
            const s = (c.status || "").toUpperCase();
            if (s === "INITIATED" || s === "DPC_REVIEW" || s === "DRAFT") {
              return "Generated";
            }
            if (s === "COMPETENT_AUTHORITY_APPROVAL" || s === "APPROVED" || s === "ACTIVE") {
              return "Sent";
            }
            return "Acknowledged";
          })();

          return {
            id: String(c.cycleId),
            orderNumber: `DMRC/HR/PROM/${fyYear}/${c.cycleId.toString().padStart(3, '0')}`,
            cycleId: String(c.promotionCycleId || `PRO${fyYear}-${c.cycleId.toString().padStart(3, '0')}`),
            issueDate: new Date().toLocaleDateString('en-US'),
            employees: Number(count),
            status: statusVal,
          };
        })
      );

      if (updatedList.length > 0) {
        setOrders(updatedList);
      } else {
        // Fallback check for cycle 1 if list is empty
        try {
          const genRes = await Api.get(
            API_URLS.PROMOTION.GENERATE_OFFICE_ORDER,
            [{ cycleId: Number(selectedCycleId) }],
            true
          );
          const genEmps = genRes?.data?.data?.promotedEmployees || [];
          setOrders([
            {
              id: String(selectedCycleId),
              orderNumber: `DMRC/HR/PROM/2025/001`,
              cycleId: `PRO2025-001`,
              issueDate: new Date().toLocaleDateString('en-US'),
              employees: genEmps.length || 20,
              status: "Generated"
            }
          ]);
        } catch (e) {
          setOrders([]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch promoted counts:", error);
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedCycleId]);

  useEffect(() => {
    fetchPromotedCounts();
  }, [fetchPromotedCounts]);

  const handleGenerateOfficeOrder = async () => {
    setGeneratingOrder(true);
    try {
      console.log("Generating office order for cycleId:", selectedCycleId);
      const response = await Api.get(
        API_URLS.PROMOTION.GENERATE_OFFICE_ORDER,
        [{ cycleId: selectedCycleId }],
        true
      );
      console.log("GENERATE_OFFICE_ORDER response:", response);
      toast.success("Office order generated successfully!");

      const empList = response?.data?.data?.promotedEmployees || [];
      setModalEmployees(empList);
      setActiveModalCycleId(String(selectedCycleId));
      setShowModal(true);
      fetchPromotedCounts();
    } catch (error: any) {
      console.error("Failed to generate office order", error);
      toast.error("Failed to generate office order: " + Api.getErrorMessage(error));
    } finally {
      setGeneratingOrder(false);
    }
  };

  const handleViewEmployees = async (cycleId: number) => {
    if (fetchingDetails) return;
    setFetchingDetails(true);
    try {
      console.log("Fetching employees list for cycleId:", cycleId);
      const response = await Api.get(
        API_URLS.PROMOTION.GENERATE_OFFICE_ORDER,
        [{ cycleId: Number(cycleId) }],
        true
      );
      console.log("GENERATE_OFFICE_ORDER response (View):", response);
      const empList = response?.data?.data?.promotedEmployees || [];
      setModalEmployees(empList);
      setActiveModalCycleId(String(cycleId));
      setShowModal(true);
    } catch (error: any) {
      console.error("Failed to fetch employees list", error);
      toast.error("Failed to fetch employees: " + Api.getErrorMessage(error));
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleDownloadHtml = async (cycleId: string, orderNumber: string) => {
    try {
      const token = tokenService.getToken();
      const headers: any = {
        "Accept": "text/html",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${BASE_URLS.BASE_URL}promotion/generate-office-order-html?cycleId=${cycleId}`, {
        method: "GET",
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }

      const htmlContent = await response.text();
      
      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `office_order_promotion_cycle_${cycleId}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Office order HTML downloaded successfully!");
    } catch (error: any) {
      console.error("Failed to download HTML", error);
      toast.error("Failed to download office order HTML: " + error.message);
    }
  };

  const handleDownloadPdf = async (cycleId: string, orderNumber: string) => {
    try {
      const token = tokenService.getToken();
      const headers: any = {
        "Accept": "text/html",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${BASE_URLS.BASE_URL}promotion/generate-office-order-html?cycleId=${cycleId}`, {
        method: "GET",
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }

      const rawHtml = await response.text();
      const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ["style", "link", "meta", "title"],
        ADD_ATTR: ["target", "rel", "href", "class", "style"]
      });
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(sanitizedHtml);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
      toast.success("Office order PDF print window opened successfully!");
    } catch (error: any) {
      console.error("Failed to download PDF", error);
      toast.error("Failed to download office order PDF: " + error.message);
    }
  };

  const handleDownloadEmployeePdf = async (cycleId: string, empId: string) => {
    try {
      const token = tokenService.getToken();
      const headers: any = {
        "Accept": "text/html",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${BASE_URLS.BASE_URL}promotion/generate-office-order-html?cycleId=${cycleId}&empId=${empId}`, {
        method: "GET",
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }

      const rawHtml = await response.text();
      const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ["style", "link", "meta", "title"],
        ADD_ATTR: ["target", "rel", "href", "class", "style"]
      });
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(sanitizedHtml);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
      toast.success(`Office order PDF print window for ${empId} opened!`);
    } catch (error: any) {
      console.error("Failed to download PDF", error);
      toast.error(`Failed to download office order PDF for ${empId}: ` + error.message);
    }
  };

  const handleViewEmployeeDocument = async (cycleId: string, empId: string) => {
    const viewWindow = window.open("", "_blank");
    if (!viewWindow) {
      toast.warning("Please allow pop-ups to view the office order document.");
      return;
    }

    viewWindow.opener = null;
    viewWindow.document.write("<p style='font-family: Arial, sans-serif; padding: 20px;'>Loading document...</p>");

    try {
      const token = tokenService.getToken();
      const headers: any = {
        "Accept": "text/html",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${BASE_URLS.BASE_URL}promotion/generate-office-order-html?cycleId=${cycleId}&empId=${empId}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to load: ${response.statusText}`);
      }

      const rawHtml = await response.text();
      const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ["style", "link", "meta", "title"],
        ADD_ATTR: ["target", "rel", "href", "class", "style"],
      });

      viewWindow.document.open();
      viewWindow.document.write(sanitizedHtml);
      viewWindow.document.close();
      viewWindow.focus();
    } catch (error: any) {
      viewWindow.close();
      console.error("Failed to view office order document", error);
      toast.error(`Failed to view office order document for ${empId}: ` + error.message);
    }
  };

  const handleBulkDownloadAll = () => {
    const targetCycleId = String(selectedCycleId);
    if (!targetCycleId) {
      toast.warning("Please select a valid promotion cycle.");
      return;
    }
    handleDownloadPdf(targetCycleId, `DMRC_HR_PROM_CYCLE_${targetCycleId}`);
  };

  const filtered = useMemo(() => {
    if (!query) return orders;
    const q = query.toLowerCase();
    return orders.filter(
      (o) => o.orderNumber.toLowerCase().includes(q) || o.cycleId.toLowerCase().includes(q)
    );
  }, [orders, query]);

  const stats = useMemo(() => ({
    total: orders.length,
    sent: orders.filter(o => o.status === "Sent").length,
    acknowledged: orders.filter(o => o.status === "Acknowledged").length,
  }), [orders]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Sent": return "badge bg-info";
      case "Acknowledged": return "badge bg-success";
      case "Generated": return "badge bg-secondary";
      default: return "badge bg-light text-dark border";
    }
  };

  /* ---------- CONFIGURATIONS ---------- */
  const dashboardSections = [
    {
      title: "Overview",
      tiles: [
        { title: "Total Orders Generated", icon: "ti-file-text", count: stats.total, link: "#", colorClass: "text-primary" },
        { title: "Sent to Employees", icon: "ti-mail", count: stats.sent, link: "#", colorClass: "text-success" },
        { title: "Acknowledged", icon: "ti-check", count: stats.acknowledged, link: "#", colorClass: "text-purple" },
      ]
    }
  ];

  const columns = [
    {
      title: "Order Number",
      dataIndex: "orderNumber",
      render: (val: string) => <div className="fw-medium text-dark">{val}</div>,
    },
    { title: "Cycle ID", dataIndex: "cycleId" },
    { title: "Issue Date", dataIndex: "issueDate", className: "text-center" },
    {
      title: "Employees",
      dataIndex: "employees",
      className: "text-center",
      render: (n: number, record: OfficeOrder) => (
        <button
          className="badge bg-light text-dark border hover-badge-button"
          style={{ cursor: fetchingDetails ? "wait" : "pointer", border: "1px solid #ccc", padding: "4px 8px", borderRadius: "4px" }}
          onClick={() => handleViewEmployees(Number(record.id))}
          disabled={fetchingDetails}
        >
          {n} employee(s)
        </button>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      className: "text-center",
      render: (s: OfficeOrder['status']) => <span className={getStatusBadgeClass(s)}>{s}</span>,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      className: "text-end",
      render: (_: any, record: OfficeOrder) => (
        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-icon btn-sm btn-outline-primary"
            title="View Promoted Employees"
            onClick={() => handleViewEmployees(Number(record.id))}
            disabled={fetchingDetails}
            style={{ cursor: fetchingDetails ? "wait" : "pointer" }}
          >
            <i className="ti ti-eye"></i>
          </button>
          <button
            className="btn btn-icon btn-sm btn-outline-danger"
            title="Download PDF (All Promoted Employees)"
            onClick={() => handleDownloadPdf(record.id, record.orderNumber)}
          >
            <i className="ti ti-download"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Office Orders Management</h3>
            <p className="text-muted small mb-0">View and manage promotion office orders</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              value={selectedCycleId}
              onChange={(e) => setSelectedCycleId(Number(e.target.value))}
              style={{ minWidth: 240, height: 36 }}
            >
              {cyclesList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-warning text-white btn-sm"
              onClick={handleGenerateOfficeOrder}
              disabled={generatingOrder}
              style={{ height: 36, whiteSpace: 'nowrap' }}
            >
              {generatingOrder ? "Generating..." : "Generate Office Order"}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h5 className="mb-1">Office Orders</h5>
                <div className="text-muted small">Search and manage generated office orders</div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                  onClick={handleBulkDownloadAll}
                  title="Download office order PDF for all promoted employees"
                >
                  <i className="ti ti-download me-1"></i>
                  Bulk Download PDF
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                  onClick={() => handleDownloadHtml(String(selectedCycleId), "all_employees")}
                  title="Download HTML office order document"
                >
                  <i className="ti ti-file-text me-1"></i>
                  Bulk HTML
                </button>
              </div>
            </div>

            <div className="mb-4 d-flex align-items-center justify-content-between">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by order number or cycle ID..."
                className="form-control w-50"
                style={{ maxWidth: 360 }}
              />
            </div>

            <Table
              columns={columns}
              dataSource={filtered}
              paginationConfig={pagination}
              loading={loadingOrders}
              Selection={false}
              scroll={{ x: "max-content" }}
              searchPlaceholder="Search orders"
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
            />
          </div>
        </div>

        {/* Modal to display Promoted Employees */}
        {showModal && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                <div className="modal-header bg-success text-white border-0 py-3" style={{ borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
                  <h5 className="modal-title fw-bold text-white d-flex align-items-center gap-2">
                    <i className="ti ti-circle-check" style={{ fontSize: "20px" }}></i>
                    Office Orders Generated ({modalEmployees.length})
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0 text-secondary text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>
                      Promoted Employees ({modalEmployees.length})
                    </h6>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 py-1 px-2.5"
                        style={{ fontSize: "12px", borderRadius: "5px" }}
                        onClick={() => handleDownloadPdf(activeModalCycleId, "all_promoted")}
                        title="Download/Print full office order PDF for all promoted employees"
                      >
                        <i className="ti ti-download" style={{ fontSize: "13px" }}></i>
                        Download All Orders (PDF)
                      </button>
                    </div>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: "350px" }}>
                    <table className="table table-hover table-striped mb-0" style={{ fontSize: "13px" }}>
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Employee ID</th>
                          <th>Office Order No</th>
                          <th>Status</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalEmployees.map((emp, index) => (
                          <tr key={emp.empId || index}>
                            <td className="fw-semibold text-dark">{emp.empId}</td>
                            <td>
                              <code className="text-primary fw-medium">{emp.officeOrderNo}</code>
                            </td>
                            <td>
                              <span className="badge bg-success-light text-success text-uppercase fw-semibold" style={{ fontSize: "11px" }}>
                                PROMOTED
                              </span>
                            </td>
                            <td className="text-end">
                              <div className="d-inline-flex align-items-center gap-1">
                                <button
                                  className="btn btn-icon btn-sm btn-outline-primary"
                                  title="View Document"
                                  aria-label={`View office order document for ${emp.empId}`}
                                  onClick={() => handleViewEmployeeDocument(activeModalCycleId, emp.empId)}
                                  style={{ width: "28px", height: "28px" }}
                                >
                                  <i className="ti ti-eye" style={{ fontSize: "12px" }}></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-sm btn-outline-danger"
                                  title="Download PDF"
                                  aria-label={`Download office order document for ${emp.empId}`}
                                  onClick={() => handleDownloadEmployeePdf(activeModalCycleId, emp.empId)}
                                  style={{ width: "28px", height: "28px" }}
                                >
                                  <i className="ti ti-download" style={{ fontSize: "12px" }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-success btn-sm px-3 d-inline-flex align-items-center gap-1"
                    onClick={() => handleDownloadPdf(activeModalCycleId, "all_promoted")}
                  >
                    <i className="ti ti-download"></i>
                    Download All Office Orders
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm px-4"
                    onClick={() => setShowModal(false)}
                    style={{ borderRadius: "6px" }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
        .btn-icon {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 4px;
        }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
        .hover-badge-button:hover {
          background-color: #f0f2f5 !important;
          color: #0d6efd !important;
          border-color: #0d6efd !important;
          transition: all 0.2s ease-in-out;
        }
      `}</style>
      </div>
    </div>
  );
};

export default OfficeOrders;

